/**
 * 商务风尚模版 - 导航栏
 *
 * 设计理念：沉稳商务风，深墨绿 + 香槟金
 * - 顶部香槟金细线，深墨绿实底导航（区别于 sunset 的浅色玻璃态通栏）
 * - 金色高亮：active 下划线、发布按钮、登录描边
 * - 方角 + 细边框，商务正式感
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function ExecutiveNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const { siteId, loading: siteLoading, siteName: contextSiteName } = useSite();
  const { siteConfig, navMenus, navCategories } = useSiteConfig();
  const enableVip = siteConfig?.enable_vip ?? true;

  const siteName = contextSiteName;

  useEffect(() => {
    if (!siteConfig) return;
    if (siteConfig.logo) {
      setLogo(siteConfig.logo);
      setLogoError(false);
    }
  }, [siteConfig]);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate("/");
  };

  const isHome = location.pathname === "/";

  const isNavActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* 顶部香槟金细线 */}
      <div className="h-[3px] bg-gradient-to-r from-[#C2A04B] via-[#DDC583] to-[#C2A04B]" />

      <nav className="sticky top-0 z-50 bg-[#1F4E3D] shadow-[0_6px_24px_-8px_rgba(15,36,27,0.45)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[4.2rem]">

            {/* ── 左侧：Logo + 站点名 ── */}
            <Link to="/" className="flex items-center gap-3 shrink-0 group">
              {logo && !logoError ? (
                <img
                  src={logo}
                  alt={siteName}
                  className="h-10 w-auto object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div className="h-10 w-10 rounded-md bg-gradient-to-br from-[#C2A04B] to-[#8C6E2E] flex items-center justify-center text-white font-bold text-base shadow-md border border-[#DDC583]/40">
                  {siteName ? siteName.charAt(0).toUpperCase() : 'E'}
                </div>
              )}
              {siteName && (
                <span className="text-lg font-bold text-white tracking-tight group-hover:text-[#DDC583] transition-colors">
                  {siteName}
                </span>
              )}
            </Link>

            {/* ── 中间：主导航（桌面端）── */}
            <div className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className={`relative px-4 py-2.5 text-[15px] font-medium transition-colors ${
                  isHome
                    ? "text-[#DDC583]"
                    : "text-white/85 hover:text-white"
                }`}
              >
                首页
                <span className={`absolute left-4 right-4 -bottom-0.5 h-[2px] bg-[#C2A04B] transition-all ${isHome ? "opacity-100" : "opacity-0"}`} />
              </Link>
              {navMenus.map((menu) => (
                <Link
                  key={menu.id}
                  to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                       menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                       menu.type === 'link' ? (menu.target_url || '#') : '#'}
                  className={`relative px-4 py-2.5 text-[15px] font-medium transition-colors ${
                    isNavActive(`/category/${menu.target_url || ''}`) || isNavActive(`/page/${menu.target_url || ''}`)
                      ? "text-[#DDC583]"
                      : "text-white/85 hover:text-white"
                  }`}
                >
                  {menu.name}
                  <span className={`absolute left-4 right-4 -bottom-0.5 h-[2px] bg-[#C2A04B] transition-all ${isNavActive(`/category/${menu.target_url || ''}`) || isNavActive(`/page/${menu.target_url || ''}`) ? "opacity-100" : "opacity-0"}`} />
                </Link>
              ))}
              {navCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`relative px-4 py-2.5 text-[15px] font-medium transition-colors ${
                    location.pathname === `/category/${cat.slug}`
                      ? "text-[#DDC583]"
                      : "text-white/85 hover:text-white"
                  }`}
                >
                  {cat.name}
                  <span className={`absolute left-4 right-4 -bottom-0.5 h-[2px] bg-[#C2A04B] transition-all ${location.pathname === `/category/${cat.slug}` ? "opacity-100" : "opacity-0"}`} />
                </Link>
              ))}
            </div>

            {/* ── 右侧：用户区 + 发布按钮 ── */}
            <div className="flex items-center gap-3">
              {isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/10 transition-colors border border-white/10"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C2A04B] to-[#8C6E2E] flex items-center justify-center text-white text-xs font-bold overflow-hidden shadow-sm">
                      {user?.avatar ? (
                        <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        (user?.nickname || 'U').charAt(0).toUpperCase()
                      )}
                    </div>
                    <span className="text-sm text-white/85 hidden md:inline max-w-[80px] truncate">
                      {user?.nickname}
                    </span>
                    <i className="ri-arrow-down-s-line text-white/50 text-xs" />
                  </button>

                  {isUserMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsUserMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-48 bg-[#0F241B] rounded-lg shadow-2xl border border-[#C2A04B]/30 py-2 z-20">
                        <Link
                          to="/dashboard"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:bg-white/5 hover:text-[#DDC583] transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="ri-user-line text-base" />会员中心
                        </Link>
                        <Link
                          to="/dashboard/my-listings"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:bg-white/5 hover:text-[#DDC583] transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="ri-file-list-line text-base" />我的发布
                        </Link>
                        {enableVip && (
                          <Link
                            to="/vip"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#DDC583] font-medium hover:bg-white/5 transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <i className="ri-vip-crown-line text-base" />开通VIP
                          </Link>
                        )}
                        {isAdmin && (
                          <>
                            <div className="border-t border-white/10 my-1" />
                            <Link
                              to="/admin"
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:bg-white/5 hover:text-[#DDC583] transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <i className="ri-shield-star-line text-base" />后台管理
                            </Link>
                          </>
                        )}
                        <div className="border-t border-white/10 my-1" />
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/60 hover:bg-white/5 hover:text-red-300 transition-colors w-full text-left"
                        >
                          <i className="ri-logout-box-line text-base" />退出登录
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => navigate("/login")}
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-[#DDC583] rounded-md border border-[#C2A04B]/50 hover:bg-white/10 transition-colors"
                >
                  <i className="ri-user-line" />
                  <span>登录</span>
                </button>
              )}

              <button
                onClick={() => navigate("/publish")}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-bold bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] rounded-md hover:from-[#D8B86A] hover:to-[#B8912F] transition-all shadow-lg shadow-[#C2A04B]/20"
              >
                <i className="ri-add-line text-base" />
                <span className="hidden sm:inline">发布信息</span>
              </button>

              {/* 移动端菜单按钮 */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2.5 rounded-md hover:bg-white/10 transition-colors"
              >
                <i className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line text-xl text-white/85`} />
              </button>
            </div>
          </div>
        </div>

        {/* ── 移动端菜单 ── */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-white/10 bg-[#17382B] shadow-lg">
            <div className="px-4 py-3 space-y-1 max-h-[60vh] overflow-y-auto">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 text-[15px] font-medium rounded-md transition-colors ${
                  isHome ? "text-[#DDC583] bg-white/10" : "text-white/85 hover:bg-white/5"
                }`}
              >
                <i className="ri-home-4-line text-lg" />首页
              </Link>
              {navMenus.map((menu) => (
                <Link
                  key={menu.id}
                  to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                       menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                       menu.type === 'link' ? (menu.target_url || '#') : '#'}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-white/85 rounded-md hover:bg-white/5 transition-colors"
                >
                  <i className="ri-menu-line text-lg" />{menu.name}
                </Link>
              ))}
              {navCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 text-[15px] font-medium rounded-md transition-colors ${
                    location.pathname === `/category/${cat.slug}` ? "text-[#DDC583] bg-white/10" : "text-white/85 hover:bg-white/5"
                  }`}
                >
                  <i className="ri-grid-line text-lg" />{cat.name}
                </Link>
              ))}

              {enableVip && (
                <Link
                  to="/vip"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-[#DDC583] rounded-md hover:bg-white/5 transition-colors"
                >
                  <i className="ri-vip-crown-line text-lg" />开通VIP
                </Link>
              )}

              <div className="border-t border-white/10 my-2" />

              {isLoggedIn ? (
                <>
                  <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-white/85 rounded-md hover:bg-white/5 transition-colors">
                    <i className="ri-user-line text-lg" />会员中心
                  </Link>
                  <Link to="/dashboard/my-listings" onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-white/85 rounded-md hover:bg-white/5 transition-colors">
                    <i className="ri-file-list-line text-lg" />我的发布
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-white/85 rounded-md hover:bg-white/5 transition-colors">
                      <i className="ri-shield-star-line text-lg" />后台管理
                    </Link>
                  )}
                  <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-white/60 rounded-md hover:bg-white/5 transition-colors w-full">
                    <i className="ri-logout-box-line text-lg" />退出登录
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-[#DDC583] rounded-md hover:bg-white/5 transition-colors">
                  <i className="ri-user-line text-lg" />登录 / 注册
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
